import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useEffect, useState } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { Au08InputSchema } from "@aucash/rules/au08";

import { useAuth } from "../../src/auth";
import { AppBar, Button, Coin, Eyebrow } from "../../src/components/brand";
import {
  ButtonGrid,
  Field,
  MoneyInput,
  OptionList,
  SliderInput,
  Stepper,
  YesNo,
} from "../../src/components/controls";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, webNoOutline } from "../../src/theme";

// AU-08 — Private Health (LHC + MLS). Progressive, one-question-per-screen flow
// recreated from the design handoff (Health Calculator.html). The maths still
// lives in @aucash/rules and runs on the API — this screen only collects state,
// persists it (so /compare can personalise its banner from the same answers),
// then posts to calc.au08 and hands the result screen an id.

type Situation = "single" | "couple" | "family";
type YesNoVal = "yes" | "no";
type CoverCont = "before" | "later" | "unsure";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"] as const;
const CURRENT_YEAR = new Date().getFullYear();
const STORAGE_KEY = "mba_au08_v1"; // shared with apps/marketing/src/pages/compare.astro

interface FlowState {
  started: boolean;
  step: number;
  age: number;
  situation: Situation | null;
  income: string;
  partner: string;
  kids: number;
  hasCover: YesNoVal | null;
  partnerCover: YesNoVal | null;
  coverCont: CoverCont | null;
  coverYear: string;
  region: (typeof STATES)[number] | null;
}

const DEFAULT_STATE: FlowState = {
  started: false,
  step: 0,
  age: 34,
  situation: null,
  income: "",
  partner: "",
  kids: 1,
  hasCover: null,
  partnerCover: null,
  coverCont: null,
  coverYear: "",
  region: null,
};

type StepKey =
  | "age"
  | "situation"
  | "kids"
  | "income"
  | "cover"
  | "partnerCover"
  | "coverCont"
  | "region";

function stepsFor(s: FlowState): StepKey[] {
  const list: StepKey[] = ["age", "situation"];
  if (s.situation === "family") list.push("kids");
  list.push("income", "cover");
  if (s.hasCover === "yes") list.push("coverCont");
  // Couple/family filers: the surcharge exemption needs the whole household
  // (you, partner, dependants) covered, so the partner's cover changes the
  // household picture even when you're personally covered.
  if (s.situation !== "single") list.push("partnerCover");
  list.push("region");
  return list;
}

function isValid(key: StepKey, s: FlowState): boolean {
  switch (key) {
    case "age":
      return s.age >= 18 && s.age <= 80;
    case "situation":
      return s.situation !== null;
    case "kids":
      return s.kids >= 1;
    case "income":
      return Number(s.income) > 0;
    case "cover":
      return s.hasCover !== null;
    case "partnerCover":
      return s.partnerCover !== null;
    case "coverCont":
      return s.coverCont !== null;
    case "region":
      return s.region !== null;
    default:
      return true;
  }
}

/** Persist the flow on web only — the Compare hub (Astro, web) reads the same
 * localStorage key to personalise its banner. Native has no Compare hub. */
function persist(s: FlowState): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        started: s.started,
        step: s.step,
        age: s.age,
        situation: s.situation,
        income: s.income,
        partner: s.partner,
        kids: s.kids,
        hasCover: s.hasCover,
        partnerCover: s.partnerCover,
        coverCont: s.coverCont,
        region: s.region,
      }),
    );
  } catch {
    /* storage may be unavailable (private mode) — non-fatal */
  }
}

export default function Index(): JSX.Element {
  const router = useRouter();
  const { status } = useAuth();
  const [s, setS] = useState<FlowState>(DEFAULT_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    persist(s);
  }, [s]);

  const set = (patch: Partial<FlowState>): void =>
    setS((prev) => ({ ...prev, ...patch }));

  const reset = (): void => {
    setSubmitError(null);
    setS(DEFAULT_STATE);
  };

  const steps = stepsFor(s);
  const onIntro = !s.started;
  const progress = onIntro ? 0 : (s.step + 1) / steps.length;

  const submit = (): void => {
    const holds = s.hasCover === "yes";
    let firstHeldYear: number | "unsure" | null;
    if (!holds) {
      firstHeldYear = null;
    } else if (s.coverCont === "before") {
      // Held since before turning 31 → first-held age ≤ 30 → no LHC loading.
      firstHeldYear = CURRENT_YEAR - s.age + 30;
    } else if (s.coverCont === "later") {
      const y = Number(s.coverYear);
      firstHeldYear =
        s.coverYear !== "" && Number.isInteger(y) && y >= 1960 && y <= CURRENT_YEAR
          ? y
          : "unsure";
    } else {
      firstHeldYear = "unsure";
    }

    const parsed = Au08InputSchema.safeParse({
      age: s.age,
      filingStatus: s.situation === "single" ? "single" : "family",
      income: Number(s.income),
      partnerIncome: s.situation !== "single" ? Number(s.partner || "0") : 0,
      children: s.situation === "family" ? s.kids : 0,
      holdsHospitalCover: holds,
      partnerHoldsHospitalCover:
        s.situation !== "single" ? s.partnerCover === "yes" : false,
      firstHeldYear,
    });
    if (!parsed.success || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    trpc.calc.au08
      .mutate({ input: parsed.data })
      .then(({ id }) => router.push({ pathname: "/au08/result", params: { id } }))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't save your result.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar
        pill="Private health"
        onRestart={s.started ? reset : undefined}
        progress={s.started ? progress : undefined}
      />

      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          {onIntro ? (
            <Intro authStatus={status} onStart={() => set({ started: true, step: 0 })} onAuth={() => router.push("/auth")} onResults={() => router.push("/my-results")} onCalculators={() => router.push("/")} />
          ) : (
            <Step
              s={s}
              steps={steps}
              set={set}
              onBack={() => set({ step: s.step - 1 })}
              onNext={() => {
                if (s.step === steps.length - 1) submit();
                else set({ step: s.step + 1 });
              }}
              submitting={submitting}
              submitError={submitError}
            />
          )}
        </View>
      </ScrollView>
    </View>
  );
}

function Intro({
  authStatus,
  onStart,
  onAuth,
  onResults,
  onCalculators,
}: {
  authStatus: ReturnType<typeof useAuth>["status"];
  onStart: () => void;
  onAuth: () => void;
  onResults: () => void;
  onCalculators: () => void;
}): JSX.Element {
  return (
    <View style={styles.intro}>
      <View style={styles.introNav}>
        <Pressable onPress={onCalculators} accessibilityRole="button">
          <Text style={styles.authLink}>← Calculators</Text>
        </Pressable>
        {authStatus === "authed" ? (
          <Pressable onPress={onResults} accessibilityRole="button">
            <Text style={styles.authLink}>My results</Text>
          </Pressable>
        ) : authStatus === "anon" ? (
          <Pressable onPress={onAuth} accessibilityRole="button">
            <Text style={styles.authLink}>Sign in</Text>
          </Pressable>
        ) : null}
      </View>
      <Coin size={72} />
      <Text style={styles.introTitle}>
        Are you paying for private health you don't need — or missing cover you
        do?
      </Text>
      <Text style={styles.introBody}>
        Answer a few questions and we'll work out your Medicare Levy Surcharge
        and Lifetime Health Cover loading for FY26 — in plain English.
      </Text>
      <View style={styles.chipRow}>
        {["~ 60 seconds", "No login", "No TFN"].map((c) => (
          <View key={c} style={styles.metaChip}>
            <Text style={styles.metaChipText}>{c}</Text>
          </View>
        ))}
      </View>
      <Button label="Start the check →" onPress={onStart} full testID="intro-start" />
      <Text style={styles.fine}>
        Information only · not financial advice · sources shown with your result
      </Text>
    </View>
  );
}

function Step({
  s,
  steps,
  set,
  onBack,
  onNext,
  submitting,
  submitError,
}: {
  s: FlowState;
  steps: StepKey[];
  set: (patch: Partial<FlowState>) => void;
  onBack: () => void;
  onNext: () => void;
  submitting: boolean;
  submitError: string | null;
}): JSX.Element {
  const key = steps[s.step]!;
  const last = s.step === steps.length - 1;
  const valid = isValid(key, s);
  const fam = s.situation !== "single";

  return (
    <View style={styles.step}>
      <Text style={styles.qcount}>
        Question {s.step + 1} of {steps.length}
      </Text>

      {key === "age" ? (
        <Question
          title="How old are you?"
          help="Your age sets the Lifetime Health Cover clock — the loading grows for every year past 31 you go without hospital cover."
        >
          <SliderInput
            value={s.age}
            onChange={(age) => set({ age })}
            min={18}
            max={80}
            unit="years"
            maxLabel="80+"
            accessibilityLabel="Your age in years"
          />
        </Question>
      ) : null}

      {key === "situation" ? (
        <Question
          title="Who are we working this out for?"
          help="The Medicare Levy Surcharge uses a higher income threshold for couples and families."
        >
          <OptionList<Situation>
            options={[
              { value: "single", label: "Just me", sub: "Single, no dependants" },
              { value: "couple", label: "Me and my partner", sub: "A couple, no kids" },
              { value: "family", label: "Family with kids", sub: "One or more dependent children" },
            ]}
            value={s.situation}
            onChange={(situation) => set({ situation })}
            testIDPrefix="situation"
          />
        </Question>
      ) : null}

      {key === "kids" ? (
        <Question
          title="How many dependent children?"
          help="The family surcharge threshold rises by $1,500 for each child after the first."
        >
          <Stepper value={s.kids} onChange={(kids) => set({ kids })} min={1} max={6} testID="kids" />
        </Question>
      ) : null}

      {key === "income" ? (
        <Question
          title={fam ? "What's your household income?" : "What's your income this year?"}
          help="Use your income for Medicare Levy Surcharge purposes — taxable income plus reportable fringe benefits, super contributions and investment income."
        >
          <Field label={fam ? "Your income (for FY26)" : "Income for FY26"}>
            <MoneyInput
              value={s.income}
              onChange={(income) => set({ income })}
              testID="input-income"
              accessibilityLabel={fam ? "Your income for FY26" : "Income for FY26"}
            />
          </Field>
          {fam ? (
            <Field label="Partner's income (optional)">
              <MoneyInput
                value={s.partner}
                onChange={(partner) => set({ partner })}
                testID="input-partner"
                accessibilityLabel="Partner's income for FY26"
              />
            </Field>
          ) : null}
        </Question>
      ) : null}

      {key === "cover" ? (
        <Question
          title="Do you currently hold private hospital cover?"
          help="Extras-only policies don't count — the surcharge exemption needs hospital cover."
        >
          <YesNo
            value={s.hasCover === null ? null : s.hasCover === "yes"}
            onChange={(v) => set({ hasCover: v ? "yes" : "no" })}
            testIDPrefix="cover"
            yesLabel="Yes, I have hospital cover"
            yesSub="A private hospital policy (not just extras)"
            noLabel="No, I don't"
            noSub="No private hospital cover right now"
          />
        </Question>
      ) : null}

      {key === "partnerCover" ? (
        <Question
          title="Does your partner hold private hospital cover?"
          help="The surcharge exemption applies per person — to remove it for the whole household the ATO needs you, your partner and any dependants covered."
        >
          <YesNo
            value={s.partnerCover === null ? null : s.partnerCover === "yes"}
            onChange={(v) => set({ partnerCover: v ? "yes" : "no" })}
            testIDPrefix="partner-cover"
            yesLabel="Yes, my partner has hospital cover"
            yesSub="A private hospital policy (not just extras)"
            noLabel="No, or I'm not sure"
            noSub="We'll treat the household as not fully covered"
          />
        </Question>
      ) : null}

      {key === "coverCont" ? (
        <Question
          title="Have you held hospital cover continuously since before you turned 31?"
          help="This is what decides your Lifetime Health Cover loading."
        >
          <OptionList<CoverCont>
            options={[
              { value: "before", label: "Yes — since before I turned 31", sub: "You likely have no LHC loading" },
              { value: "later", label: "No — I took it out later", sub: "You may be paying an LHC loading" },
              { value: "unsure", label: "I'm not sure", sub: "We'll show you how to check" },
            ]}
            value={s.coverCont}
            onChange={(coverCont) => set({ coverCont })}
            testIDPrefix="cover-cont"
          />
          {s.coverCont === "later" ? (
            <Field
              label="Roughly which year did you take it out? (optional)"
              help="If you know it, we can be exact about your loading. Leave it blank and we'll show how to check."
            >
              <TextInput
                value={s.coverYear}
                onChangeText={(t) => set({ coverYear: t.replace(/[^0-9]/g, "") })}
                keyboardType="number-pad"
                maxLength={4}
                placeholder="e.g. 2014"
                placeholderTextColor={colors.muted}
                style={styles.yearInput}
                testID="input-cover-year"
                accessibilityLabel="Year you first took out hospital cover"
              />
            </Field>
          ) : null}
        </Question>
      ) : null}

      {key === "region" ? (
        <Question
          title="Which state or territory?"
          help="This tailors the funds and information we show with your result."
        >
          <ButtonGrid<(typeof STATES)[number]>
            options={STATES}
            value={s.region}
            onChange={(region) => set({ region })}
            testIDPrefix="state"
          />
        </Question>
      ) : null}

      <View style={styles.navRow}>
        <Button
          label={submitting ? "Saving…" : last ? "See my result →" : "Continue →"}
          onPress={onNext}
          disabled={!valid || submitting}
          testID="step-next"
        />
        {s.step > 0 ? (
          <Button label="Back" variant="ghost" onPress={onBack} testID="step-back" />
        ) : null}
      </View>
      {submitError !== null ? (
        <Text style={styles.submitError}>{submitError}</Text>
      ) : null}
    </View>
  );
}

function Question({
  title,
  help,
  children,
}: {
  title: string;
  help?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <View style={styles.question}>
      <Text style={styles.qtitle}>{title}</Text>
      {help !== undefined ? <Text style={styles.qhelp}>{help}</Text> : null}
      <View style={styles.qbody}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 680 },

  // intro
  intro: { gap: space.lg, paddingTop: space.xl, alignItems: "flex-start" },
  introNav: {
    alignSelf: "stretch",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  authLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  introTitle: {
    fontFamily: fonts.display,
    fontSize: 34,
    lineHeight: 38,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.sm,
  },
  introBody: { fontFamily: fonts.body, fontSize: 17, lineHeight: 25, color: colors.inkSoft },
  chipRow: { flexDirection: "row", flexWrap: "wrap", gap: space.sm, marginVertical: space.sm },
  metaChip: {
    backgroundColor: colors.sand,
    borderRadius: radius.pill,
    paddingVertical: 6,
    paddingHorizontal: 13,
  },
  metaChipText: { fontFamily: fonts.bodySemibold, fontSize: 12.5, color: colors.inkSoft },
  fine: { fontFamily: fonts.mono, fontSize: 11.5, color: colors.muted, marginTop: space.xs },

  // steps
  step: { gap: space.lg, paddingTop: space.lg },
  qcount: {
    fontFamily: fonts.mono,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: colors.muted,
  },
  question: { gap: space.md },
  qtitle: {
    fontFamily: fonts.display,
    fontSize: 28,
    lineHeight: 32,
    letterSpacing: -0.8,
    color: colors.ink,
  },
  qhelp: { fontFamily: fonts.body, fontSize: 15, lineHeight: 22, color: colors.inkSoft },
  qbody: { gap: space.lg, marginTop: space.xs },

  yearInput: {
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radius.md,
    paddingVertical: 14,
    paddingHorizontal: space.lg,
    fontFamily: fonts.bodyMedium,
    fontSize: 18,
    color: colors.ink,
    backgroundColor: colors.card,
    ...webNoOutline,
  },

  navRow: { flexDirection: "row", alignItems: "center", gap: space.md, marginTop: space.sm },
  submitError: { fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.danger },
});
